import React, { useMemo, useRef, useState } from 'react';
import { Box, Typography, Button } from '@mui/material';
import PrototypeLayout from '../PrototypeLayout';
import { colors, font, type as ty, layout } from '../theme/tokens';
import { bode, cone, BODE_BRANDED } from '../theme/bodeTokens';
import {
  panel, Badge, PageHeader, PanelTitle, DemoFooter, Tone,
  Tooltip, TipState, emptyTip,
} from '../components/ui';
import {
  WINDOW_DAYS, FORECAST_DAYS, ADVISORY_CURRENT,
  PeriodKey, PeriodSelection, defaultPeriod,
  DoorRow, Recommendation, RecPriority,
  getDoorRows, getTrend, getRecommendations, getTriage,
  formatDay, indicesFor,
} from '../data/maintenance';
import { useFleet } from '../FleetContext';
import PeriodSwitch from '../components/PeriodSwitch';
import { useI18n } from '../i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Prototype 05 — Maintenance & action recommendations.
//
// The page is built around one argument: the statistics stay deterministic and
// auditable on the left, the agent orchestrates on the right. The seam between
// the two columns is the point, so it is labelled rather than blended.
//
// This is also the only tab carrying the requested display changes — new door
// labels, selectable cycle period, Bode palette — so it can be compared
// against tabs 01–04, which are untouched.
// ─────────────────────────────────────────────────────────────────────────────

// ── Helpers ──────────────────────────────────────────────────────────────────

function healthColor(h: number): string {
  if (h >= 85) return colors.ok;
  if (h >= 70) return colors.warn;
  return colors.alert;
}

function priorityTone(p: RecPriority): Tone {
  return p === 'high' ? 'alert' : p === 'medium' ? 'warn' : 'neutral';
}

const nf = (n: number, locale: string) => n.toLocaleString(locale);

// ── Trend chart ──────────────────────────────────────────────────────────────
// One series. History solid, forecast dashed inside a widening cone — the cone
// is the point: accuracy degrades with horizon, direction holds.

const TrendChart: React.FC<{ unitId: string; doorId: string }> = ({ unitId, doorId }) => {
  const { t, locale } = useI18n();
  const [tip, setTip] = useState<TipState>(emptyTip);
  const svgRef = useRef<SVGSVGElement>(null);

  const trend = useMemo(() => getTrend(unitId, doorId), [unitId, doorId]);

  const W = 640, H = 236;
  const m = { top: 16, right: 16, bottom: 26, left: 40 };
  const iw = W - m.left - m.right;
  const ih = H - m.top - m.bottom;

  const lastI = WINDOW_DAYS - 1 + FORECAST_DAYS;
  const values = [
    ...trend.history.map(h => h.value),
    ...trend.forecast.map(f => f.hi),
    ...trend.forecast.map(f => f.lo),
    ADVISORY_CURRENT,
  ];
  const yMin = Math.floor((Math.min(...values) - 0.2) * 10) / 10;
  const yMax = Math.ceil((Math.max(...values) + 0.2) * 10) / 10;

  const x = (i: number) => m.left + (i / lastI) * iw;
  const y = (v: number) => m.top + ih - ((v - yMin) / (yMax - yMin)) * ih;

  const histPath = trend.history.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.value).toFixed(1)}`).join(' ');
  const midPath = trend.forecast.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.mid).toFixed(1)}`).join(' ');
  const conePath =
    trend.forecast.map((p, k) => `${k === 0 ? 'M' : 'L'}${x(p.i).toFixed(1)},${y(p.hi).toFixed(1)}`).join(' ') +
    ' ' +
    [...trend.forecast].reverse().map(p => `L${x(p.i).toFixed(1)},${y(p.lo).toFixed(1)}`).join(' ') +
    ' Z';

  const ticks = [0, 30, 60, 89, lastI];

  const onMove = (e: React.MouseEvent<SVGSVGElement>) => {
    const svg = svgRef.current;
    if (!svg) return;
    const r = svg.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * W;
    const i = Math.round(((px - m.left) / iw) * lastI);
    if (i < 0 || i > lastI) { setTip(emptyTip); return; }

    if (i <= WINDOW_DAYS - 1) {
      const p = trend.history[i];
      setTip({
        visible: true,
        x: e.clientX + 14,
        y: e.clientY - 12,
        content: `${formatDay(p.i, locale)}\n${t('p5.trend.observed')}  ${p.value.toFixed(2)} A`,
      });
    } else {
      const f = trend.forecast[i - (WINDOW_DAYS - 1)];
      if (!f) { setTip(emptyTip); return; }
      setTip({
        visible: true,
        x: e.clientX + 14,
        y: e.clientY - 12,
        content: `+${i - (WINDOW_DAYS - 1)} ${t('p5.trend.daysOut')}\n${t('p5.trend.mid')}  ${f.mid.toFixed(2)} A\n${t('p5.trend.band')}  ${f.lo.toFixed(2)}–${f.hi.toFixed(2)} A`,
      });
    }
  };

  return (
    <Box>
      <Box
        component="svg"
        ref={svgRef}
        viewBox={`0 0 ${W} ${H}`}
        onMouseMove={onMove}
        onMouseLeave={() => setTip(emptyTip)}
        sx={{ width: '100%', height: 'auto', display: 'block', cursor: 'crosshair' }}
        role="img"
        aria-label={t('p5.trend.title')}
      >
        {/* Grid — recessive */}
        {[0, 0.25, 0.5, 0.75, 1].map(f => {
          const v = yMin + (yMax - yMin) * f;
          return (
            <g key={f}>
              <line
                x1={m.left} x2={W - m.right}
                y1={y(v)} y2={y(v)}
                stroke={colors.border} strokeWidth={1}
              />
              <text
                x={m.left - 8} y={y(v) + 3}
                textAnchor="end"
                fill={colors.inkFaint}
                style={{ fontFamily: font.mono, fontSize: '9px' }}
              >
                {v.toFixed(1)}
              </text>
            </g>
          );
        })}

        {/* Forecast cone */}
        <path d={conePath} fill={cone.band} opacity={0.75} />

        {/* Advisory threshold */}
        <line
          x1={m.left} x2={W - m.right}
          y1={y(ADVISORY_CURRENT)} y2={y(ADVISORY_CURRENT)}
          stroke={colors.warn} strokeWidth={1.5} strokeDasharray="5 3"
        />
        <text
          x={W - m.right} y={y(ADVISORY_CURRENT) - 6}
          textAnchor="end"
          fill={colors.warn}
          style={{ fontFamily: font.mono, fontSize: '9px', letterSpacing: '0.06em' }}
        >
          {t('p5.trend.advisory')} {ADVISORY_CURRENT.toFixed(1)} A
        </text>

        {/* Today divider */}
        <line
          x1={x(WINDOW_DAYS - 1)} x2={x(WINDOW_DAYS - 1)}
          y1={m.top} y2={m.top + ih}
          stroke={colors.borderStrong} strokeWidth={1}
        />
        <text
          x={x(WINDOW_DAYS - 1) + 5} y={m.top + 9}
          fill={colors.inkFaint}
          style={{ fontFamily: font.mono, fontSize: '9px', letterSpacing: '0.06em' }}
        >
          {t('p5.trend.today')}
        </text>

        {/* Series */}
        <path d={histPath} fill="none" stroke={cone.line} strokeWidth={2} strokeLinejoin="round" />
        <path d={midPath} fill="none" stroke={cone.lineDashed} strokeWidth={2} strokeDasharray="4 4" />

        {/* Axis */}
        <line x1={m.left} x2={W - m.right} y1={m.top + ih} y2={m.top + ih} stroke={colors.borderStrong} strokeWidth={1} />
        {ticks.map(i => (
          <text
            key={i}
            x={x(i)} y={H - 8}
            textAnchor={i === 0 ? 'start' : i === lastI ? 'end' : 'middle'}
            fill={colors.inkFaint}
            style={{ fontFamily: font.mono, fontSize: '9px' }}
          >
            {i <= WINDOW_DAYS - 1 ? formatDay(i, locale) : `+${i - (WINDOW_DAYS - 1)}d`}
          </text>
        ))}
      </Box>

      {/* Direct labels rather than a legend box — one series plus its band */}
      <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mt: 1.5 }}>
        {[
          { c: cone.line, dash: false, label: t('p5.trend.observed') },
          { c: cone.lineDashed, dash: true, label: t('p5.trend.projected') },
          { c: cone.band, dash: false, label: t('p5.trend.band'), block: true },
        ].map(item => (
          <Box key={item.label} sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
            <Box sx={{
              width: 16,
              height: item.block ? 9 : 0,
              borderTop: item.block ? 'none' : `2px ${item.dash ? 'dashed' : 'solid'} ${item.c}`,
              backgroundColor: item.block ? item.c : 'transparent',
              border: item.block ? `1px solid ${cone.bandEdge}` : undefined,
              flexShrink: 0,
            }} />
            <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint }}>
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>

      <Tooltip tip={tip} />
    </Box>
  );
};

// ── Door table ───────────────────────────────────────────────────────────────

const DoorTable: React.FC<{
  rows: DoorRow[];
  selected: string;
  onSelect: (id: string) => void;
  periodKey: PeriodKey;
}> = ({ rows, selected, onSelect, periodKey }) => {
  const { t, locale } = useI18n();
  const maxCycles = Math.max(...rows.map(r => r.cycles), 1);

  return (
    <Box>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: '54px 1fr 84px 62px',
        gap: 1,
        px: 1.25,
        pb: 0.75,
        borderBottom: `1px solid ${colors.border}`,
      }}>
        {['p5.table.door', 'p5.table.cycles', 'p5.table.current', 'p5.table.health'].map((k, i) => (
          <Typography
            key={k}
            sx={{
              ...ty.eyebrow,
              fontSize: '9px',
              color: colors.inkFaint,
              textAlign: i >= 2 ? 'right' : 'left',
            }}
          >
            {t(k)}
          </Typography>
        ))}
      </Box>

      <Box sx={{ maxHeight: 316, overflowY: 'auto' }}>
        {rows.map(r => {
          const active = r.id === selected;
          return (
            <Box
              key={r.id}
              onClick={() => onSelect(r.id)}
              role="button"
              tabIndex={0}
              onKeyDown={(e: React.KeyboardEvent) => {
                if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onSelect(r.id); }
              }}
              sx={{
                display: 'grid',
                gridTemplateColumns: '54px 1fr 84px 62px',
                gap: 1,
                alignItems: 'center',
                px: 1.25,
                py: 1,
                cursor: 'pointer',
                outline: 'none',
                borderBottom: `1px solid ${colors.border}`,
                borderLeft: `3px solid ${active ? bode.blue : 'transparent'}`,
                backgroundColor: active ? colors.blueWash : 'transparent',
                '&:hover, &:focus-visible': { backgroundColor: active ? colors.blueWash : colors.bgAlt },
              }}
            >
              <Typography sx={{
                fontFamily: font.mono, fontSize: '12px', fontWeight: 700,
                color: active ? bode.blue : colors.ink,
              }}>
                {r.label}
              </Typography>

              {/* Magnitude — bar plus value, so the period change is visible */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
                <Box sx={{
                  flex: 1,
                  height: 6,
                  minWidth: 24,
                  backgroundColor: colors.bgAlt,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '2px',
                  overflow: 'hidden',
                }}>
                  <Box sx={{
                    width: `${Math.round((r.cycles / maxCycles) * 100)}%`,
                    height: '100%',
                    backgroundColor: active ? bode.blue : colors.blueMid,
                  }} />
                </Box>
                <Typography sx={{
                  fontFamily: font.mono, fontSize: '11px', color: colors.inkMuted,
                  width: 52, textAlign: 'right', flexShrink: 0,
                }}>
                  {nf(r.cycles, locale)}
                </Typography>
              </Box>

              <Typography sx={{
                fontFamily: font.mono, fontSize: '11px',
                color: r.current >= ADVISORY_CURRENT ? colors.alert : colors.inkMuted,
                textAlign: 'right',
              }}>
                {r.current.toFixed(2)} A
              </Typography>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                <Typography sx={{
                  fontFamily: font.mono, fontSize: '12px', fontWeight: 700,
                  color: healthColor(r.health),
                }}>
                  {r.health}
                </Typography>
              </Box>
            </Box>
          );
        })}
      </Box>

      <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint, mt: 1.25 }}>
        {t('p5.table.note', { period: t(`period.${periodKey}`).toLowerCase() })}
      </Typography>
    </Box>
  );
};

// ── Recommendation card ──────────────────────────────────────────────────────

const RecCard: React.FC<{ rec: Recommendation }> = ({ rec }) => {
  const { t } = useI18n();
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{
      ...panel,
      padding: 0,
      overflow: 'hidden',
      borderLeft: `3px solid ${rec.priority === 'high' ? colors.alert : rec.priority === 'medium' ? colors.warn : colors.borderStrong}`,
    }}>
      {/* Head */}
      <Box sx={{ px: 2.25, pt: 2, pb: 1.75 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.25, flexWrap: 'wrap' }}>
          <Badge label={t(`p5.kind.${rec.kind}`)} t={rec.kind === 'external' ? 'neutral' : 'info'} />
          <Badge label={t(`p5.prio.${rec.priority}`)} t={priorityTone(rec.priority)} />
          <Box sx={{ flex: 1 }} />
          <Typography sx={{ ...ty.meta, fontSize: '10px', color: colors.inkFaint }}>
            {rec.doorLabel}
          </Typography>
        </Box>

        <Typography sx={{
          fontFamily: font.sans, fontSize: '15px', fontWeight: 600,
          letterSpacing: '-0.01em', color: colors.ink, lineHeight: 1.45,
        }}>
          {t(rec.titleKey, rec.vars)}
        </Typography>
      </Box>

      {/* Evidence */}
      <Box sx={{ px: 2.25, pb: 1.75 }}>
        <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 1 }}>
          {t('p5.rec.evidence')}
        </Typography>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
          {rec.evidence.map((e, i) => (
            <Box key={i} sx={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 0.75,
              px: 1,
              py: 0.6,
              borderRadius: layout.radius,
              backgroundColor: e.deterministic ? colors.blueWash : colors.bgAlt,
              border: `1px solid ${e.deterministic ? colors.blueSoft : colors.border}`,
            }}>
              <Typography sx={{ ...ty.meta, fontSize: '9px', color: colors.inkFaint }}>
                {t(e.labelKey)}
              </Typography>
              <Typography sx={{
                fontFamily: font.sans, fontSize: '12px', fontWeight: 600, color: colors.ink,
              }}>
                {e.valueIsKey ? t(e.value) : e.value}
              </Typography>
              <Typography sx={{ ...ty.meta, fontSize: '9px', color: colors.inkGhost }}>
                {t(e.sourceKey)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      {/* Why — the reasoning trace */}
      <Box sx={{ px: 2.25, pb: 1.5 }}>
        <Button
          onClick={() => setOpen(o => !o)}
          disableRipple
          aria-expanded={open}
          sx={{
            px: 0, py: 0.25, minWidth: 0,
            textTransform: 'none',
            fontFamily: font.sans, fontSize: '12px', fontWeight: 600,
            color: bode.blue,
            '&:hover': { backgroundColor: 'transparent', color: bode.blueDeep },
          }}
        >
          {open ? t('p5.rec.hideWhy') : t('p5.rec.why')} {open ? '▲' : '▼'}
        </Button>

        {open && (
          <Box sx={{
            mt: 1,
            pl: 1.75,
            borderLeft: `2px solid ${colors.blueSoft}`,
            display: 'flex',
            flexDirection: 'column',
            gap: 1,
          }}>
            {rec.reasoning.map((step, i) => (
              <Box key={i} sx={{ display: 'flex', gap: 1.25 }}>
                <Typography sx={{
                  ...ty.meta, fontSize: '10px', color: colors.inkGhost, flexShrink: 0, pt: '2px',
                }}>
                  {String(i + 1).padStart(2, '0')}
                </Typography>
                <Typography sx={{
                  fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted, lineHeight: 1.6,
                }}>
                  {t(step.textKey, step.vars)}
                </Typography>
              </Box>
            ))}
          </Box>
        )}
      </Box>

      {/* Action — the paperwork */}
      <Box sx={{
        px: 2.25, py: 1.75,
        backgroundColor: bode.agentGround,
        borderTop: `1px solid ${bode.agentBorder}`,
      }}>
        <Box sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          gap: 1.5, mb: rec.checklistKeys.length ? 1.25 : 0, flexWrap: 'wrap',
        }}>
          <Box>
            <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 0.5 }}>
              {t('p5.rec.slot')}
            </Typography>
            <Typography sx={{
              fontFamily: font.sans, fontSize: '13px', fontWeight: 600, color: colors.ink,
            }}>
              {rec.slot.durationMin > 0
                ? `${rec.slot.date} · ${rec.slot.depot} · ${rec.slot.durationMin} ${t('p5.rec.minutes')}`
                : t(rec.slot.reasonKey)}
            </Typography>
            <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint, mt: 0.25 }}>
              {rec.slot.durationMin > 0 ? t(rec.slot.reasonKey) : ''}
            </Typography>
          </Box>
          <Typography sx={{ ...ty.meta, fontSize: '10px', color: colors.inkFaint }}>
            {t('p5.rec.order')} {rec.orderRef}
          </Typography>
        </Box>

        {rec.checklistKeys.length > 0 && (
          <Box>
            <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 0.75 }}>
              {t('p5.rec.checklist')}
            </Typography>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
              {rec.checklistKeys.map(k => (
                <Box key={k} sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                  <Box sx={{
                    width: 11, height: 11, mt: '3px', flexShrink: 0,
                    border: `1px solid ${colors.borderStrong}`,
                    borderRadius: '2px',
                    backgroundColor: colors.bg,
                  }} />
                  <Typography sx={{ fontFamily: font.sans, fontSize: '12px', color: colors.inkMuted }}>
                    {t(k)}
                  </Typography>
                </Box>
              ))}
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

// ── Page ─────────────────────────────────────────────────────────────────────

const Prototype5: React.FC = () => {
  const { unitId, caption } = useFleet();
  const { t, locale } = useI18n();

  const [period, setPeriod] = useState<PeriodSelection>(defaultPeriod);
  const [selectedDoor, setSelectedDoor] = useState<string | null>(null);

  const rows = useMemo(() => getDoorRows(unitId, period), [unitId, period]);
  const recs = useMemo(() => getRecommendations(unitId, locale), [unitId, locale]);
  const triage = useMemo(() => getTriage(unitId), [unitId]);

  const doorId = selectedDoor && rows.some(r => r.id === selectedDoor)
    ? selectedDoor
    : rows[0].id;
  const trend = useMemo(() => getTrend(unitId, doorId), [unitId, doorId]);
  const row = rows.find(r => r.id === doorId)!;

  const dayCount = indicesFor(period).length;

  return (
    <PrototypeLayout protoId="05" titleKey="proto.05.title">
      <PageHeader
        eyebrow={t('p5.approach')}
        title={t('p5.title')}
        right={
          <>
            <Badge label={t('common.syntheticData')} t="info" />
            <Badge label={t('p5.illustrative')} t="accent" />
            <Typography sx={{ ...ty.meta, color: colors.inkFaint }}>
              {caption}
            </Typography>
          </>
        }
      />

      {/* Scope statement — sets expectations before anything else is read */}
      <Box sx={{
        ...panel,
        mb: 3,
        borderLeft: `3px solid ${bode.rule}`,
        backgroundColor: colors.blueWash,
      }}>
        <Typography sx={{
          fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted, lineHeight: 1.7,
        }}>
          {t('p5.scope')}
        </Typography>
      </Box>

      {/* Triage strip */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(3, 1fr)' },
        gap: 2,
        mb: 3,
      }}>
        {[
          { key: 'flags', value: triage.flags, tone: 'neutral' as Tone },
          { key: 'actionable', value: triage.actionable, tone: 'alert' as Tone },
          { key: 'suppressed', value: triage.suppressed, tone: 'ok' as Tone },
        ].map(s => (
          <Box key={s.key} sx={{
            ...panel,
            padding: '14px 18px',
            display: 'flex',
            alignItems: 'baseline',
            gap: 1.5,
          }}>
            <Typography sx={{
              fontFamily: font.sans, fontSize: '28px', fontWeight: 700,
              letterSpacing: '-0.02em', lineHeight: 1,
              color: s.tone === 'alert' ? colors.alert : s.tone === 'ok' ? colors.ok : colors.ink,
            }}>
              {s.value}
            </Typography>
            <Box>
              <Typography sx={{ fontFamily: font.sans, fontSize: '13px', fontWeight: 600, color: colors.ink }}>
                {t(`p5.triage.${s.key}`)}
              </Typography>
              <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint }}>
                {t(`p5.triage.${s.key}.sub`)}
              </Typography>
            </Box>
          </Box>
        ))}
      </Box>

      {/* ── The seam: deterministic left, orchestration right ───────────── */}
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
        gap: 3,
        mb: 3,
      }}>
        {/* Deterministic column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={panel}>
            <PanelTitle right={<Badge label={t('p5.det.label')} t="neutral" />}>
              {t('p5.det.title')}
            </PanelTitle>
            <Typography sx={{
              fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted, mb: 2, lineHeight: 1.6,
            }}>
              {t('p5.det.intro')}
            </Typography>

            <PeriodSwitch sel={period} onChange={setPeriod} accent={bode.blue} accentDeep={bode.blueDeep} />

            <DoorTable
              rows={rows}
              selected={doorId}
              onSelect={setSelectedDoor}
              periodKey={period.key}
            />
          </Box>

          <Box sx={panel}>
            <PanelTitle right={<Badge label={row.label} t="info" />}>
              {t('p5.trend.title')}
            </PanelTitle>

            <Box sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 1.5,
              mb: 2.5,
            }}>
              {[
                {
                  label: t('p5.trend.rate'),
                  value: `+${trend.ratePerWeek.toFixed(2)}`,
                  sub: t('p5.trend.rateSub'),
                  color: trend.ratePerWeek > 0.02 ? colors.warn : colors.ink,
                },
                {
                  label: t('p5.trend.spread'),
                  value: `±${(trend.horizonSpread / 2).toFixed(2)}`,
                  sub: t('p5.trend.spreadSub', { days: FORECAST_DAYS }),
                  color: colors.ink,
                },
                {
                  label: t('p5.trend.advisoryIn'),
                  value: trend.daysToAdvisory !== null ? `${trend.daysToAdvisory} d` : '—',
                  sub: trend.daysToAdvisory !== null ? t('p5.trend.withinHorizon') : t('p5.trend.beyondHorizon'),
                  color: trend.daysToAdvisory !== null ? colors.alert : colors.inkFaint,
                },
              ].map(s => (
                <Box key={s.label} sx={{
                  backgroundColor: colors.bgAlt,
                  border: `1px solid ${colors.border}`,
                  borderRadius: layout.radius,
                  p: 1.5,
                }}>
                  <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 0.75 }}>
                    {s.label}
                  </Typography>
                  <Typography sx={{
                    fontFamily: font.sans, fontSize: '20px', fontWeight: 700,
                    letterSpacing: '-0.02em', color: s.color, lineHeight: 1,
                  }}>
                    {s.value}
                  </Typography>
                  <Typography sx={{ fontFamily: font.sans, fontSize: '11px', color: colors.inkFaint, mt: 0.5 }}>
                    {s.sub}
                  </Typography>
                </Box>
              ))}
            </Box>

            <TrendChart unitId={unitId} doorId={doorId} />

            <Typography sx={{
              fontFamily: font.sans, fontSize: '12px', color: colors.inkFaint,
              mt: 2, pt: 2, borderTop: `1px solid ${colors.border}`, lineHeight: 1.6,
            }}>
              {t('p5.trend.caveat')}
            </Typography>
          </Box>
        </Box>

        {/* Orchestration column */}
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Box sx={{
            ...panel,
            backgroundColor: bode.agentGround,
            borderColor: bode.agentBorder,
          }}>
            <PanelTitle right={<Badge label={t('p5.agent.label')} t="info" />}>
              {t('p5.agent.title')}
            </PanelTitle>
            <Typography sx={{
              fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted, lineHeight: 1.7,
            }}>
              {t('p5.agent.intro')}
            </Typography>
          </Box>

          {recs.map(rec => <RecCard key={rec.id} rec={rec} />)}
        </Box>
      </Box>

      {/* Architecture strip */}
      <Box sx={panel}>
        <PanelTitle>{t('p5.arch.title')}</PanelTitle>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr', md: 'repeat(2,1fr)', lg: 'repeat(4,1fr)' },
          gap: 3,
        }}>
          {['1', '2', '3', '4'].map((n, i) => (
            <Box key={n} sx={{
              pl: 2,
              borderLeft: `3px solid ${i < 2 ? colors.borderStrong : bode.rule}`,
            }}>
              <Typography sx={{ ...ty.eyebrow, fontSize: '9px', color: colors.inkFaint, mb: 1 }}>
                {i < 2 ? t('p5.det.label') : t('p5.agent.label')}
              </Typography>
              <Typography sx={{
                fontFamily: font.sans, fontSize: '14px', fontWeight: 700,
                letterSpacing: '-0.01em', color: colors.ink, mb: 1, lineHeight: 1.3,
              }}>
                {t(`p5.arch.${n}.title`)}
              </Typography>
              <Typography sx={{ fontFamily: font.sans, fontSize: '13px', color: colors.inkMuted, lineHeight: 1.6 }}>
                {t(`p5.arch.${n}.body`)}
              </Typography>
            </Box>
          ))}
        </Box>
      </Box>

      <DemoFooter
        note={`${t('common.syntheticNote')} · ${t(`period.${period.key}`)} (${dayCount} d) · ${BODE_BRANDED ? 'Bode' : t('p5.paletteProvisional')}`}
      />
    </PrototypeLayout>
  );
};

export default Prototype5;
